import { useQuery } from 'react-query';
import _isEmpty from 'lodash/isEmpty';
import { INITIATE_WITHDRAW } from '../appConstants/APIEndpoint';
import { requestApiPost } from './index';
import { useUserStore } from '../storage/userState';
import { useTokenStore } from '../storage/tokenState';
import { setWithdrawSession } from '../storage/withdrawState';
import { EBankStatus } from '../storage/store';
import type {
  IInitiateWithdrawRequest,
  IWithdrawScreenData,
} from '../interfaces/Withdrawal';

/**
 * Opens a withdrawal session — step 1 of the flow.
 * Mirrors `WithdrawalViewModel.initWithdrawScreen` / `getInitWithdrawalReqObj`.
 *
 * Everything the screen renders (title, limits, saved modes, initial breakdown)
 * comes back here, along with the `sessionId` every later call needs.
 */
export const initiateWithdrawal = async (
  body: IInitiateWithdrawRequest,
): Promise<IWithdrawScreenData> => {
  const res = await requestApiPost<IWithdrawScreenData>(INITIATE_WITHDRAW, body);
  const response = res?.response;

  if (!response?.success) {
    throw new Error(response?.error || 'Could not open withdrawal');
  }
  return response?.data ?? {};
};

interface IUseInitiateWithdrawal {
  /** Prefilled amount to re-open the session with. Not part of the query key —
   *  typing must not re-initialise the session, that is the breakdown call. */
  userInputAmount?: number;
  enabled?: boolean;
}

export const useInitiateWithdrawal = ({
  userInputAmount,
  enabled = true,
}: IUseInitiateWithdrawal = {}) => {
  const { userDetails } = useUserStore();
  const { token: userToken } = useTokenStore();

  const actualCoins = userDetails?.actualCoins ?? 0;
  const bankStatus = userDetails?.bankDetails?.status;

  const body: IInitiateWithdrawRequest = {
    actualCoins,
    // Android treats a pending bank as available — `getInitWithdrawalReqObj`.
    isBankAvailable:
      bankStatus === EBankStatus.SUCCESS || bankStatus === EBankStatus.PENDING,
    isUpiAvailable: !_isEmpty(userDetails?.upiId),
    showOnlyCtd: false,
    phoneNumber: userDetails?.phone ?? '',
    localeSelected: null,
    userInputAmount: userInputAmount ?? null,
  };

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['initiateWithdrawal', userToken?._id],
    queryFn: () => initiateWithdrawal(body),
    // Wait for user details: opening a session with a placeholder balance of 0
    // makes the server compute the wrong limits. Android guards the same way,
    // returning null from `getInitWithdrawalReqObj` without payments data.
    enabled: enabled && !_isEmpty(userToken) && !_isEmpty(userDetails),
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (screen) => setWithdrawSession(screen?.screenData?.sessionId),
  });

  return {
    screenData: data,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};

export default useInitiateWithdrawal;
